import { useState, useRef, useEffect } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

interface ImageCropperProps {
  imageSrc: string;
  aspectRatio: number;
  onCropComplete: (croppedImage: string) => void;
  onCancel: () => void;
  open: boolean;
  imageType?: string; // Optional parameter to specify image type
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export function ImageCropper({ 
  imageSrc, 
  aspectRatio, 
  onCropComplete, 
  onCancel, 
  open,
  imageType = 'image/jpeg' 
}: ImageCropperProps) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isLoading, setIsLoading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (open && imgRef.current) {
      const { width, height } = imgRef.current;
      setCrop(centerAspectCrop(width, height, aspectRatio));
    }
  }, [open, aspectRatio]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspectRatio));
  };

  const generateCrop = async () => {
    if (!completedCrop || !imgRef.current) return;
    
    setIsLoading(true);
    
    try {
      const image = imgRef.current;
      
      // Get the original source image dimensions
      const originalWidth = image.naturalWidth;
      const originalHeight = image.naturalHeight;
      
      // Calculate the scaling factors
      const scaleX = originalWidth / image.width;
      const scaleY = originalHeight / image.height;
      
      // Create a canvas with the exact output size
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(completedCrop.width * scaleX);
      canvas.height = Math.round(completedCrop.height * scaleY);
      
      const ctx = canvas.getContext('2d', { alpha: true });
      
      if (!ctx) {
        throw new Error('No 2d context');
      }
      
      // Use high quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Draw the cropped image at 1:1 ratio
      ctx.drawImage(
        image,
        Math.round(completedCrop.x * scaleX),
        Math.round(completedCrop.y * scaleY),
        canvas.width,
        canvas.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
      
      // Use original format with highest quality
      const mimeType = imageType.startsWith('image/') ? imageType : 'image/jpeg';
      const qualityOption = mimeType === 'image/jpeg' || mimeType === 'image/webp' ? 1.0 : undefined;
      
      const base64Image = canvas.toDataURL(mimeType, qualityOption);
      onCropComplete(base64Image);
    } catch (error) {
      console.error('Error generating crop:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onCancel()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Crop Image</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <ReactCrop
            crop={crop}
            onChange={(c: Crop) => setCrop(c)}
            onComplete={(c: PixelCrop) => setCompletedCrop(c)}
            aspect={aspectRatio}
            className="max-h-[400px] mx-auto"
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              style={{ maxHeight: '400px' }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Drag to adjust the crop area. The image will be cropped to match how it appears on your store profile.
          </p>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={generateCrop}
            disabled={!completedCrop || isLoading}
            className="bg-[#967F81] hover:bg-[#843329]"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Apply Crop'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 